import type { ReactNode } from 'react'
import { Chevron, Segmented, TypeChip, type TypeKey } from '../Page'
import { Glyph } from './Glyph'
import { TYPE_ORDER } from './model'

/**
 * The page toolbar: everything that changes what is on screen, and nothing
 * that changes what the record says.
 *
 * The six type chips are a filter over the overlay rather than over the
 * archive: a chip switched off hides that type's outlines and takes nothing
 * out of the page. The count beside each is the number of regions of that type
 * on this page, counted from the page itself.
 */

function StepButton({
  label,
  onClick,
  disabled,
  children,
  size = 26,
}: {
  label: string
  onClick: () => void
  disabled?: boolean
  children: React.ReactNode
  size?: number
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      disabled={disabled}
      className="flex items-center justify-center rounded-[--radius-md] border text-ink disabled:text-ink4"
      style={{
        width: size,
        height: size,
        borderColor: disabled ? 'transparent' : 'var(--color-neutral-400)',
      }}
    >
      {children}
    </button>
  )
}

function Divider() {
  return <div className="mx-3 h-full w-px flex-none bg-n300" />
}

export function Toolbar({
  pageLabelText,
  pageCount,
  onPrevPage,
  onNextPage,
  hasPrev,
  hasNext,
  hitIndex,
  hitTotal,
  onPrevHit,
  onNextHit,
  zoomPercent,
  onZoomIn,
  onZoomOut,
  onFit,
  overlayOn,
  onOverlay,
  counts,
  visibleTypes,
  onToggleType,
  selecting,
  onSelectRegion,
  compareHref,
  pdfHref = null,
}: {
  pageLabelText: string
  pageCount: number | null
  onPrevPage: () => void
  onNextPage: () => void
  hasPrev: boolean
  hasNext: boolean
  hitIndex: number
  hitTotal: number
  onPrevHit: () => void
  onNextHit: () => void
  zoomPercent: number
  onZoomIn: () => void
  onZoomOut: () => void
  onFit: () => void
  overlayOn: boolean
  onOverlay: (on: boolean) => void
  counts: Record<TypeKey, number>
  visibleTypes: Set<TypeKey>
  onToggleType: (type: TypeKey) => void
  selecting: boolean
  onSelectRegion: () => void
  compareHref: string
  /** The whole issue as its original PDF, opened at this page. */
  pdfHref?: string | null
}) {
  return (
    <div className="flex h-[42px] flex-none items-center overflow-x-auto border-b border-n300 bg-surface px-4">
      <div className="flex flex-none items-center gap-0.5">
        <StepButton label="Previous page" onClick={onPrevPage} disabled={!hasPrev}>
          <Chevron direction="left" />
        </StepButton>
        <div className="px-1.5 font-mono text-[12px]">{pageLabelText}</div>
        <StepButton label="Next page" onClick={onNextPage} disabled={!hasNext}>
          <Chevron direction="right" />
        </StepButton>
        {pageCount ? (
          <div className="pl-2 font-mono text-[10.5px] uppercase text-ink2">of {pageCount}</div>
        ) : null}
      </div>

      <Divider />

      <div className="flex flex-none items-center gap-2">
        <div className="chrome text-[11px]">Hit</div>
        <div className="flex items-center gap-0.5">
          <StepButton
            label="Previous search hit"
            onClick={onPrevHit}
            disabled={hitTotal === 0}
            size={24}
          >
            <Chevron direction="left" size={11} />
          </StepButton>
          <div className="px-1.5 font-mono text-[12px]">
            {hitTotal ? `${hitIndex + 1}/${hitTotal}` : '0/0'}
          </div>
          <StepButton
            label="Next search hit"
            onClick={onNextHit}
            disabled={hitTotal === 0}
            size={24}
          >
            <Chevron direction="right" size={11} />
          </StepButton>
        </div>
      </div>

      <Divider />

      <div className="flex flex-none items-center gap-2">
        <StepButton label="Zoom out" onClick={onZoomOut} size={24}>
          <Glyph name="minus" size={12} />
        </StepButton>
        <div className="w-[44px] text-center font-mono text-[12px]">{zoomPercent}%</div>
        <StepButton label="Zoom in" onClick={onZoomIn} size={24}>
          <Glyph name="plus" size={12} />
        </StepButton>
        <button type="button" className="btn-ghost pl-0.5 text-[13px]" onClick={onFit}>
          Fit
        </button>
      </div>

      <Divider />

      <div className="flex-none">
        <Segmented
          ariaLabel="Overlay"
          accent
          size="sm"
          value={overlayOn ? 'annotated' : 'clean'}
          onChange={(next) => onOverlay(next === 'annotated')}
          options={[
            { value: 'clean', label: 'Clean', title: 'The page as printed. Key O.' },
            { value: 'annotated', label: 'Annotated', title: 'Show the typed region outlines' },
          ]}
        />
      </div>

      <div className="ml-3 flex flex-none items-center gap-1.5">
        {TYPE_ORDER.map((type) => (
          <TypeChip
            key={type}
            type={type}
            count={counts[type]}
            active={visibleTypes.has(type)}
            onClick={() => onToggleType(type)}
          />
        ))}
      </div>

      <div className="ml-auto flex flex-none items-center gap-2 pl-3">
        <button
          type="button"
          className="btn-secondary gap-1.5 px-2.5 py-1"
          aria-pressed={selecting}
          onClick={onSelectRegion}
          style={
            selecting
              ? { borderColor: 'var(--jl-accent)', color: 'var(--jl-accent)' }
              : undefined
          }
        >
          Select region <span className="font-mono text-[10px] text-ink2">S</span>
        </button>
        <a href={compareHref} className="btn-secondary gap-1.5 px-2.5 py-1 no-underline">
          Compare <span className="font-mono text-[10px] text-ink2">C</span>
        </a>
        {pdfHref && (
          <a
            href={pdfHref}
            target="_blank"
            rel="noopener"
            className="btn-secondary px-2.5 py-1 no-underline"
            title="The original issue in your browser's PDF viewer, at this page"
          >
            Whole issue PDF
          </a>
        )}
      </div>
    </div>
  )
}

/**
 * The tablet's control bar, which sits under the page rather than over it.
 *
 * Landscape keeps the desktop arrangement minus the edition rail, which moves
 * behind the page-number control. Every target here is 44px, because a finger
 * is not a cursor.
 */
export function TabletBar({
  pageLabelText,
  pageCount,
  onPrevPage,
  onNextPage,
  hasPrev,
  hasNext,
  overlayOn,
  onOverlay,
  counts,
  visibleTypes,
  onToggleType,
  zoomPercent,
  onZoomIn,
  onZoomOut,
  search,
}: {
  pageLabelText: string
  pageCount: number | null
  onPrevPage: () => void
  onNextPage: () => void
  hasPrev: boolean
  hasNext: boolean
  overlayOn: boolean
  onOverlay: (on: boolean) => void
  counts: Record<TypeKey, number>
  visibleTypes: Set<TypeKey>
  onToggleType: (type: TypeKey) => void
  zoomPercent: number
  onZoomIn: () => void
  onZoomOut: () => void
  /** Search in page, which has no room in the reader bar at this width. */
  search?: ReactNode
}) {
  return (
    <div className="flex h-16 flex-none items-center gap-2.5 overflow-x-auto border-t border-n300 bg-surface px-3.5">
      <div className="flex flex-none items-center gap-0.5">
        <StepButton label="Previous page" onClick={onPrevPage} disabled={!hasPrev} size={44}>
          <Chevron direction="left" size={16} />
        </StepButton>
        <div className="px-1.5 font-mono text-[12.5px]">{pageLabelText}</div>
        <StepButton label="Next page" onClick={onNextPage} disabled={!hasNext} size={44}>
          <Chevron direction="right" size={16} />
        </StepButton>
        {pageCount ? (
          <div className="pl-1.5 font-mono text-[10.5px] uppercase text-ink2">of {pageCount}</div>
        ) : null}
      </div>

      <div className="h-7 w-px flex-none bg-n300" />

      <div className="flex-none">
        <Segmented
          ariaLabel="Overlay"
          accent
          value={overlayOn ? 'annotated' : 'clean'}
          onChange={(next) => onOverlay(next === 'annotated')}
          options={[
            { value: 'clean', label: 'Clean' },
            { value: 'annotated', label: 'Annotated' },
          ]}
        />
      </div>

      {search}

      <div className="flex flex-none items-center gap-1.5">
        {TYPE_ORDER.slice(0, 3).map((type) => (
          <TypeChip
            key={type}
            type={type}
            count={counts[type]}
            active={visibleTypes.has(type)}
            onClick={() => onToggleType(type)}
          />
        ))}
      </div>

      <div className="ml-auto flex flex-none items-center gap-1.5">
        <div className="font-mono text-[11.5px] text-ink2">{zoomPercent}%</div>
        <StepButton label="Zoom out" onClick={onZoomOut} size={44}>
          <Glyph name="minus" size={15} />
        </StepButton>
        <StepButton label="Zoom in" onClick={onZoomIn} size={44}>
          <Glyph name="plus" size={15} />
        </StepButton>
      </div>
    </div>
  )
}
